import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Location } from '../types';
import { apiService } from '../services/api';

// Marker icon for selected location
const markerIcon = new Icon({
  iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="10" r="8" fill="#3B82F6" stroke="#ffffff" stroke-width="2"/>
      <circle cx="12" cy="10" r="4" fill="#1E40AF"/>
      <path d="M12 18 L8 22 L16 22 Z" fill="#3B82F6"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Special marker icon for SV Buttercup boat
const buttercupIcon = new Icon({
  iconUrl: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="16" y="20" text-anchor="middle" fill="#10B981" font-size="24" font-weight="bold">⛵</text>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

interface MapClickHandlerProps {
  onLocationSelect: (location: Location) => void;
  centerLocation?: Location;
  buttercupLocation?: Location;
  shouldRecenter?: boolean;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onLocationSelect, centerLocation, buttercupLocation, shouldRecenter = false }) => {
  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onLocationSelect({
        latitude: lat,
        longitude: lng,
        address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
      });
    }
  });

  // Update map center when location changes (only for search-based changes)
  React.useEffect(() => {
    if (centerLocation && shouldRecenter) {
      map.setView([centerLocation.latitude, centerLocation.longitude], 12);
    }
  }, [centerLocation, map, shouldRecenter]);

  // Update map center when buttercup location is clicked
  React.useEffect(() => {
    if (buttercupLocation) {
      map.setView([buttercupLocation.latitude, buttercupLocation.longitude], 14);
    }
  }, [buttercupLocation, map]);

  return null;
};

interface MapLocationPickerProps {
  label: string;
  location: Location | null;
  onLocationChange: (location: Location | null) => void;
  placeholder?: string;
  useCurrentLocation?: boolean;
  disabled?: boolean;
  className?: string;
  autoShowMap?: boolean;
  autoGetCurrentLocation?: boolean;
}

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  label,
  location,
  onLocationChange,
  placeholder = "Enter an address or click on the map...",
  useCurrentLocation = false,
  disabled = false,
  className = "",
  autoShowMap = false,
  autoGetCurrentLocation = false
}) => {
  const [address, setAddress] = useState(location?.address || '');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(autoShowMap);
  const [suggestions, setSuggestions] = useState<Location[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [buttercupLocation, setButtercupLocation] = useState<Location | null>(null);
  const [isFetchingButtercup, setIsFetchingButtercup] = useState(false);
  const [centerOnButtercup, setCenterOnButtercup] = useState<Location | null>(null);
  const [shouldRecenterMap, setShouldRecenterMap] = useState(false);

  // Default center (Redwood City, CA)
  const defaultCenter = location 
    ? [location.latitude, location.longitude] as [number, number]
    : [37.4852, -122.2364] as [number, number];

  // Update address when location prop changes
  useEffect(() => {
    if (location?.address) {
      // Check if this is SV Buttercup location and normalize the display name
      if (location.address.includes('Buttercup') || location.address.includes('buttercup')) {
        setAddress('SV Buttercup');
      } else {
        setAddress(location.address);
      }
    }
  }, [location]);

  // Fetch SV Buttercup location on component mount
  useEffect(() => {
    const fetchButtercupLocation = async () => {
      setIsFetchingButtercup(true);
      try {
        const buttercup = await apiService.getButtercupLocation();
        if (buttercup) {
          setButtercupLocation(buttercup);
        }
      } catch (error) {
        console.warn('Failed to fetch SV Buttercup location:', error);
      } finally {
        setIsFetchingButtercup(false);
      }
    };

    fetchButtercupLocation();
  }, []); // Empty dependency array means this runs once on mount

  // Show helpful hint for current location instead of auto-requesting
  const [showLocationHint, setShowLocationHint] = useState(false);
  
  useEffect(() => {
    if (autoGetCurrentLocation && !location && useCurrentLocation) {
      // Show a hint instead of automatically requesting location
      setShowLocationHint(true);
      // Auto-hide the hint after 5 seconds
      const timer = setTimeout(() => {
        setShowLocationHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [autoGetCurrentLocation, useCurrentLocation]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.location-picker-container')) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Reset shouldRecenterMap after location changes to avoid interference
  useEffect(() => {
    if (shouldRecenterMap && location) {
      // Reset the flag after a short delay to allow the map to recenter
      const timer = setTimeout(() => {
        setShouldRecenterMap(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldRecenterMap, location]);

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    // Hide the hint when user clicks the location button
    setShowLocationHint(false);
    setIsLoadingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use the actual detected coordinates and show them clearly
          const detectedAddress = `${latitude.toFixed(4)}, ${longitude.toFixed(4)} (Your Current Location)`;
          
          const newLocation: Location = {
            latitude,
            longitude,
            address: detectedAddress,
            city: 'Current Location',
            country: 'Detected'
          };
          
          // Recenter when user gets current location
          setShouldRecenterMap(true);
          onLocationChange(newLocation);
          setAddress(detectedAddress);
        } catch (error) {
          setLocationError('Failed to get your current location');
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        setIsLoadingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location access denied by user');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('An unknown error occurred');
            break;
        }
      }
    );
  };

  const handleAddressChange = (newAddress: string) => {
    // Always update the input field immediately for responsive typing
    setAddress(newAddress);
    
    // Clear previous search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If address is too short, clear suggestions and location
    if (newAddress.trim().length <= 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      onLocationChange(null);
      setIsSearching(false);
      return;
    }
    
    // Debounce the search to avoid too many API calls
    setIsSearching(true);
    setShowSuggestions(true);
    
    const timeoutId = setTimeout(async () => {
      try {
        // Get suggestions for the typed address
        const locationSuggestions = await getLocationSuggestions(newAddress);
        setSuggestions(locationSuggestions);
        
        // Only auto-select if the user has typed a substantial amount and stopped typing
        // Removed auto-selection to avoid interfering with user input
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce delay for real API
    
    setSearchTimeout(timeoutId);
  };

  const handleMapLocationSelect = (newLocation: Location) => {
    // Show the exact coordinates that were clicked
    const clickedAddress = `${newLocation.latitude.toFixed(4)}, ${newLocation.longitude.toFixed(4)}`;
    const updatedLocation: Location = {
      ...newLocation,
      address: clickedAddress
    };
    // Don't recenter when user clicks on map
    setShouldRecenterMap(false);
    onLocationChange(updatedLocation);
    setAddress(clickedAddress);
    setLocationError(null);
    setShowSuggestions(false);
  };

  const handleSuggestionSelect = (suggestion: Location) => {
    setAddress(suggestion.address || `${suggestion.latitude.toFixed(4)}, ${suggestion.longitude.toFixed(4)}`);
    // Recenter when user selects from search results
    setShouldRecenterMap(true);
    onLocationChange(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    setLocationError(null);
  };

  const clearLocation = () => {
    setAddress('');
    onLocationChange(null);
    setLocationError(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleButtercupClick = () => {
    if (buttercupLocation) {
      // Set SV Buttercup as the selected location
      onLocationChange(buttercupLocation);
      
      // Update the search box text
      setAddress('SV Buttercup');
      
      // Clear any suggestions and errors
      setSuggestions([]);
      setShowSuggestions(false);
      setLocationError(null);
      
      // Show the map if it's not already shown
      if (!showMap) {
        setShowMap(true);
      }
      
      // Trigger map centering on Buttercup
      setCenterOnButtercup(buttercupLocation);
      
      // Reset the centering trigger after a short delay
      setTimeout(() => setCenterOnButtercup(null), 100);
    }
  };

  return (
    <div className={`space-y-3 location-picker-container relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      
      {/* Location hint for better UX */}
      {showLocationHint && useCurrentLocation && (
        <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-sm text-blue-700 dark:text-blue-300 animate-fadeIn">
          💡 Tip: Click the location icon 📍 to use your current location
        </div>
      )}
      
      {/* Address Input */}
      <div className="relative">
        <input
          type="text"
          value={address}
          onChange={(e) => handleAddressChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="block w-full px-3 py-2 pr-24 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400"
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-2">
          {isSearching && (
            <div className="loading-spinner w-4 h-4 text-blue-500 dark:text-blue-400"></div>
          )}
          
          {address && !isSearching && (
            <button
              type="button"
              onClick={clearLocation}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear location"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {useCurrentLocation && (
            <button
              type="button"
              onClick={getCurrentLocation}
              disabled={isLoadingLocation || disabled}
              className={`p-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                showLocationHint 
                  ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 animate-pulse' 
                  : 'text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300'
              }`}
              title="Use current location"
            >
              {isLoadingLocation ? (
                <div className="loading-spinner w-4 h-4"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          )}
          
          <button
            type="button"
            onClick={() => setShowMap(!showMap)}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            title="Toggle map"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </button>
        </div>

        {/* Suggestions Dropdown - Directly attached to input */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1">
            <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {isSearching && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                  <div className="loading-spinner w-3 h-3 mr-2"></div>
                  Searching locations...
                </div>
              )}
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:outline-none border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                    {suggestion.address}
                  </div>
                  {suggestion.city && suggestion.country && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {suggestion.city}, {suggestion.country}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Map */}
      {showMap && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-300">Click on the map to select a location</p>
              {isLoadingLocation && (
                <div className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                  <div className="loading-spinner w-3 h-3 mr-1"></div>
                  Detecting location...
                </div>
              )}
            </div>
          </div>
          <div className="h-64">
            <MapContainer
              center={defaultCenter}
              zoom={location ? 12 : 10}
              className="w-full h-full"
              key="map-container"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <MapClickHandler 
                onLocationSelect={handleMapLocationSelect} 
                centerLocation={location || undefined}
                buttercupLocation={centerOnButtercup || undefined}
                shouldRecenter={shouldRecenterMap}
              />
              
              {location && (
                <Marker
                  position={[location.latitude, location.longitude]}
                  icon={markerIcon}
                />
              )}
              
              {buttercupLocation && (
                <Marker
                  position={[buttercupLocation.latitude, buttercupLocation.longitude]}
                  icon={buttercupIcon}
                  title="SV Buttercup - Live Location"
                />
              )}
            </MapContainer>
          </div>
        </div>
      )}

      {/* Error Message */}
      {locationError && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {locationError}
        </p>
      )}

      {/* SV Buttercup Info */}
      {isFetchingButtercup && (
        <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded flex items-center">
          <div className="loading-spinner w-3 h-3 mr-2"></div>
          Fetching SV Buttercup live location...
        </div>
      )}
      
      {buttercupLocation && !isFetchingButtercup && (
        <button
          type="button"
          onClick={handleButtercupClick}
          className="w-full text-left text-xs text-green-600 bg-green-50 p-2 rounded hover:bg-green-100 transition-colors cursor-pointer border border-transparent hover:border-green-200"
          title="Click to center map on SV Buttercup"
        >
          ⛵ SV Buttercup: {buttercupLocation.latitude.toFixed(4)}, {buttercupLocation.longitude.toFixed(4)}
          {buttercupLocation.address && ` • ${buttercupLocation.address}`}
        </button>
      )}

      {/* Location Info */}
      {location && (
        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
          📍 {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
          {location.city && ` • ${location.city}`}
          {location.country && `, ${location.country}`}
        </div>
      )}
    </div>
  );
};

// Real location search using Nominatim (OpenStreetMap) geocoding service
const getLocationSuggestions = async (query: string): Promise<Location[]> => {
  try {
    // Use Nominatim API for geocoding (free, no API key required)
    const encodedQuery = encodeURIComponent(query);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=6&addressdetails=1&countrycodes=us,ca,gb,fr,de,it,es,au,jp,br,mx,in,sg,ae`,
      {
        headers: {
          'User-Agent': 'Neubird-Delivery-Service/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Transform Nominatim response to our Location format
    return data.map((item: any) => ({
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
      address: item.display_name,
      city: item.address?.city || item.address?.town || item.address?.village || item.address?.hamlet || '',
      country: item.address?.country || ''
    }));

  } catch (error) {
    console.warn('Geocoding service unavailable, falling back to local search:', error);
    
    // Fallback to a basic local search with common cities
    const fallbackLocations = [
      { name: 'SV Buttercup', lat: 37.512897, lng: -122.19443466666667, city: 'Redwood City', country: 'USA', address: 'SV Buttercup, Redwood City, CA, USA' },
      { name: 'Austin', lat: 30.2672, lng: -97.7431, city: 'Austin', country: 'USA', address: 'Austin, TX, USA' },
      { name: 'New York', lat: 40.7128, lng: -74.0060, city: 'New York', country: 'USA', address: 'New York, NY, USA' },
      { name: 'Los Angeles', lat: 34.0522, lng: -118.2437, city: 'Los Angeles', country: 'USA', address: 'Los Angeles, CA, USA' },
      { name: 'Chicago', lat: 41.8781, lng: -87.6298, city: 'Chicago', country: 'USA', address: 'Chicago, IL, USA' },
      { name: 'San Francisco', lat: 37.7749, lng: -122.4194, city: 'San Francisco', country: 'USA', address: 'San Francisco, CA, USA' },
      { name: 'Seattle', lat: 47.6062, lng: -122.3321, city: 'Seattle', country: 'USA', address: 'Seattle, WA, USA' },
      { name: 'Miami', lat: 25.7617, lng: -80.1918, city: 'Miami', country: 'USA', address: 'Miami, FL, USA' },
      { name: 'Dallas', lat: 32.7767, lng: -96.7970, city: 'Dallas', country: 'USA', address: 'Dallas, TX, USA' },
      { name: 'Houston', lat: 29.7604, lng: -95.3698, city: 'Houston', country: 'USA', address: 'Houston, TX, USA' },
      { name: 'Phoenix', lat: 33.4484, lng: -112.0740, city: 'Phoenix', country: 'USA', address: 'Phoenix, AZ, USA' },
      { name: 'Boston', lat: 42.3601, lng: -71.0589, city: 'Boston', country: 'USA', address: 'Boston, MA, USA' },
      { name: 'Denver', lat: 39.7392, lng: -104.9903, city: 'Denver', country: 'USA', address: 'Denver, CO, USA' },
      { name: 'Atlanta', lat: 33.7490, lng: -84.3880, city: 'Atlanta', country: 'USA', address: 'Atlanta, GA, USA' },
      { name: 'Washington DC', lat: 38.9072, lng: -77.0369, city: 'Washington', country: 'USA', address: 'Washington, DC, USA' },
      { name: 'London', lat: 51.5074, lng: -0.1278, city: 'London', country: 'UK', address: 'London, United Kingdom' },
      { name: 'Paris', lat: 48.8566, lng: 2.3522, city: 'Paris', country: 'France', address: 'Paris, France' },
      { name: 'Tokyo', lat: 35.6762, lng: 139.6503, city: 'Tokyo', country: 'Japan', address: 'Tokyo, Japan' },
      { name: 'Sydney', lat: -33.8688, lng: 151.2093, city: 'Sydney', country: 'Australia', address: 'Sydney, Australia' },
    ];

    const searchTerm = query.toLowerCase().trim();
    
    // Filter fallback locations
    const matches = fallbackLocations.filter(loc => {
      const name = loc.name.toLowerCase();
      const city = loc.city.toLowerCase();
      const country = loc.country.toLowerCase();
      const address = loc.address.toLowerCase();
      
      return name.includes(searchTerm) ||
             city.includes(searchTerm) ||
             country.includes(searchTerm) ||
             address.includes(searchTerm) ||
             name.startsWith(searchTerm) ||
             city.startsWith(searchTerm);
    });

    // Convert to Location objects
    return matches.slice(0, 6).map(loc => ({
      latitude: loc.lat,
      longitude: loc.lng,
      address: loc.address,
      city: loc.city,
      country: loc.country
    }));
  }
};





export default MapLocationPicker;
